/* eslint-disable react-refresh/only-export-components */
import { useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { alertsApi } from '@/api/clients/alertsApi'
import { analyticsApi } from '@/api/clients/analyticsApi'
import { standardsApi } from '@/api/clients/standardsApi'
import { sourcesApi } from '@/api/clients/sourcesApi'
import { policiesApi } from '@/api/clients/policiesApi'
import type { AlertGroup, StandardOption, Band } from '@/api/types'
import { Card } from '@/components/common/ui'
import { BandBadge } from '@/components/common/Badge'
import { formatDate } from '@/components/common/format'

export const KIND_ICON: Record<string, string> = {
  new_critical: 'priority_high',
  threshold_breach: 'trending_up',
  hndl_open: 'lock_clock',
}
export const KIND_LABEL: Record<string, string> = {
  new_critical: 'New critical',
  threshold_breach: 'Threshold breach',
  hndl_open: 'HNDL open',
}
export const BANDS: { key: string; label: string; hex: string }[] = [
  { key: 'critical', label: 'Critical', hex: '#C22221' },
  { key: 'high', label: 'High', hex: '#e8734f' },
  { key: 'medium', label: 'Medium', hex: '#eab308' },
  { key: 'low', label: 'Low', hex: '#94C8E4' },
  { key: 'info', label: 'Info', hex: '#94a3b8' },
]

// A priority-alert category card: icon + count header, then rows (or empty state).
export function CategoryCard({
  icon,
  title,
  count,
  tone,
  children,
  empty,
  className,
}: {
  icon: string
  title: string
  count: number
  tone: 'critical' | 'warning' | 'muted'
  children: ReactNode
  empty?: boolean
  className?: string
}) {
  const toneClass =
    tone === 'critical' ? 'text-error' : tone === 'warning' ? 'text-[#e8734f]' : 'text-subtle-light dark:text-subtle-dark'
  return (
    <div className={`bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark ${className ?? ''}`}>
      <div className="flex items-center gap-3 px-5 py-3 border-b border-border-light dark:border-border-dark">
        <span className={`material-icons ${toneClass}`}>{icon}</span>
        <span className="flex-1 text-sm font-semibold text-text-light dark:text-text-dark">{title}</span>
        <span className={`text-lg font-bold ${count > 0 ? toneClass : 'text-subtle-light dark:text-subtle-dark'}`}>
          {count}
        </span>
      </div>
      <div className="px-5 py-3">
        {empty ? <p className="text-sm text-subtle-light dark:text-subtle-dark">None.</p> : children}
      </div>
    </div>
  )
}

export function systemLink(g: AlertGroup, extra: ReactNode) {
  return (
    <li key={g.inventory_id || g.name} className="flex items-center gap-2 py-1 text-sm">
      <BandBadge band={g.worst_band as Band} />
      {g.inventory_id ? (
        <Link
          to={`/inventories/${g.inventory_id}?tab=risk`}
          className="min-w-0 flex-1 truncate text-primary dark:text-secondary hover:underline"
        >
          {g.name}
        </Link>
      ) : (
        <span className="min-w-0 flex-1 truncate text-subtle-light dark:text-subtle-dark">{g.name}</span>
      )}
      {extra}
    </li>
  )
}

// Runs the six shared queries + derives every angle. Query keys are shared with the
// dashboard/inventory pages so calling this on multiple pages does not refetch.
export function useAlertAngles(orgId: string) {
  const rollup = useQuery({ queryKey: ['alerts-rollup', orgId], queryFn: () => alertsApi.rollup(orgId), enabled: !!orgId, retry: false })
  const trends = useQuery({ queryKey: ['trends', orgId], queryFn: () => analyticsApi.trends(orgId), enabled: !!orgId, retry: false })
  const crosswalk = useQuery({ queryKey: ['crosswalk', orgId], queryFn: () => analyticsApi.crosswalk(orgId), enabled: !!orgId, retry: false })
  const standards = useQuery({ queryKey: ['standards', orgId], queryFn: () => standardsApi.list(orgId), enabled: !!orgId, retry: false })
  const orgSources = useQuery({ queryKey: ['sources-all', orgId], queryFn: () => sourcesApi.listAll(orgId), enabled: !!orgId, retry: false })
  const waivers = useQuery({ queryKey: ['waivers', orgId], queryFn: () => policiesApi.waivers(orgId), enabled: !!orgId, retry: false })

  const standardByKey = useMemo(() => {
    const m = new Map<string, StandardOption>()
    for (const s of standards.data ?? []) {
      m.set(s.name.toLowerCase(), s)
      for (const a of s.aliases ?? []) m.set(a.toLowerCase(), s)
    }
    return (k: string) => m.get(k.toLowerCase())
  }, [standards.data])

  const r = rollup.data
  const criticalSystems = (r?.systems ?? []).filter((s) => s.worst_band === 'critical')
  const hndlSystems = (r?.systems ?? []).filter((s) => s.hndl_open > 0)
  const hndlTotal = r?.by_kind?.hndl_open ?? 0

  const trendDelta = useMemo(() => {
    const pts = trends.data ?? []
    if (pts.length < 2) return null
    const last = pts[pts.length - 1]
    const prev = pts[pts.length - 2]
    const d = last.critical + last.high - (prev.critical + prev.high)
    return { d, since: prev.taken_at }
  }, [trends.data])

  const deadlines = useMemo(() => {
    const now = Date.now()
    return (crosswalk.data ?? [])
      .map((f) => {
        const std = standardByKey(f.framework)
        const dl = std?.migration_deadline
        if (!dl) return null
        const t = new Date(/^\d{4}$/.test(dl) ? `${dl}-01-01` : dl).getTime()
        const toMigrate = Math.max(0, f.in_scope - f.safe)
        if (Number.isNaN(t) || toMigrate === 0) return null
        return { key: f.framework, name: std?.name ?? f.framework, year: dl.slice(0, 4), days: Math.ceil((t - now) / 86400000), toMigrate }
      })
      .filter((d): d is NonNullable<typeof d> => d != null)
      .sort((a, b) => a.days - b.days)
  }, [crosswalk.data, standardByKey])

  const staleSources = useMemo(() => {
    const now = Date.now()
    const cut = 30 * 24 * 60 * 60 * 1000
    return (orgSources.data ?? []).filter((s) => !s.last_ingested_at || now - new Date(s.last_ingested_at).getTime() > cut)
  }, [orgSources.data])
  const expiringWaivers = useMemo(() => {
    const now = Date.now()
    const cut = 60 * 24 * 60 * 60 * 1000
    return (waivers.data ?? []).filter((w) => w.status === 'active' && new Date(w.expiry).getTime() - now <= cut)
  }, [waivers.data])
  const governanceCount = staleSources.length + expiringWaivers.length
  const bandTotal = BANDS.reduce((n, b) => n + (r?.by_band?.[b.key] ?? 0), 0)

  return {
    rollup,
    total: r?.total ?? 0,
    systems: r?.systems ?? [],
    sources: r?.sources ?? [],
    byKind: r?.by_kind ?? {},
    byBand: r?.by_band ?? {},
    bandTotal,
    criticalSystems,
    hndlSystems,
    hndlTotal,
    trendDelta,
    deadlines,
    staleSources,
    expiringWaivers,
    governanceCount,
  }
}

export type AlertAngles = ReturnType<typeof useAlertAngles>

export function RiskEscalationCard({ a, className }: { a: AlertAngles; className?: string }) {
  return (
    <CategoryCard
      icon="warning"
      title="Risk escalation"
      count={a.criticalSystems.length}
      tone="critical"
      empty={a.criticalSystems.length === 0 && !a.trendDelta}
      className={className}
    >
      {a.trendDelta && a.trendDelta.d !== 0 && (
        <p className={`mb-2 text-xs ${a.trendDelta.d > 0 ? 'text-error' : 'text-success'}`}>
          Critical + High {a.trendDelta.d > 0 ? '▲' : '▼'} {Math.abs(a.trendDelta.d)} since {formatDate(a.trendDelta.since)}
        </p>
      )}
      <ul>
        {a.criticalSystems.slice(0, 4).map((s) =>
          systemLink(s, <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">{s.crit_high} crit+high</span>),
        )}
      </ul>
    </CategoryCard>
  )
}

export function HndlHarvestCard({ a, className }: { a: AlertAngles; className?: string }) {
  return (
    <CategoryCard icon="lock_clock" title="HNDL harvest-now" count={a.hndlTotal} tone="critical" empty={a.hndlSystems.length === 0} className={className}>
      <p className="mb-2 text-xs text-subtle-light dark:text-subtle-dark">
        Assets whose Mosca window is open — data harvestable by a future quantum computer today.
      </p>
      <ul>
        {a.hndlSystems.slice(0, 4).map((s) =>
          systemLink(s, <span className="shrink-0 text-xs font-medium text-error">{s.hndl_open} open</span>),
        )}
      </ul>
    </CategoryCard>
  )
}

export function ComplianceDeadlineCard({ a, className }: { a: AlertAngles; className?: string }) {
  return (
    <CategoryCard icon="event" title="Compliance deadline" count={a.deadlines.length} tone="warning" empty={a.deadlines.length === 0} className={className}>
      <ul className="text-sm">
        {a.deadlines.slice(0, 4).map((d) => (
          <li key={d.key} className="flex items-center gap-2 py-1">
            <span className="min-w-0 flex-1 truncate font-medium text-text-light dark:text-text-dark">{d.name}</span>
            <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">{d.toMigrate} to migrate</span>
            <span className={`shrink-0 text-xs font-semibold ${d.days <= 365 ? 'text-error' : 'text-text-light dark:text-text-dark'}`}>
              {d.days < 0 ? 'overdue' : `by ${d.year}`}
            </span>
          </li>
        ))}
      </ul>
    </CategoryCard>
  )
}

export function GovernanceDriftCard({ a, className }: { a: AlertAngles; className?: string }) {
  return (
    <CategoryCard icon="policy" title="Governance drift" count={a.governanceCount} tone="warning" empty={a.governanceCount === 0} className={className}>
      <ul className="text-sm">
        {a.staleSources.slice(0, 3).map((s) => (
          <li key={s.id} className="flex items-center gap-2 py-1">
            <span className="material-icons text-[16px] text-[#e8734f] shrink-0">history</span>
            <span className="min-w-0 flex-1 truncate text-text-light dark:text-text-dark">{s.name}</span>
            <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">
              {s.last_ingested_at ? `scanned ${formatDate(s.last_ingested_at)}` : 'never scanned'}
            </span>
          </li>
        ))}
        {a.expiringWaivers.slice(0, 3).map((w) => (
          <li key={w.id} className="flex items-center gap-2 py-1">
            <span className="material-icons text-[16px] text-[#e8734f] shrink-0">gavel</span>
            <span className="min-w-0 flex-1 truncate text-text-light dark:text-text-dark capitalize">{String(w.scope_type)} waiver expiring</span>
            <span className="shrink-0 text-xs text-subtle-light dark:text-subtle-dark">exp {formatDate(w.expiry)}</span>
          </li>
        ))}
      </ul>
    </CategoryCard>
  )
}

export function AlertSummaryCard({ a, className, onKind }: { a: AlertAngles; className?: string; onKind?: (kind: string) => void }) {
  return (
    <Card title="Alert summary" className={className}>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        {['new_critical', 'threshold_breach', 'hndl_open'].map((k) => {
          const inner = (
            <>
              <span className="material-icons text-[18px] text-subtle-light dark:text-subtle-dark">{KIND_ICON[k]}</span>
              <span className="text-text-light dark:text-text-dark">{KIND_LABEL[k]}</span>
              <span className="font-semibold text-text-light dark:text-text-dark">{a.byKind[k] ?? 0}</span>
            </>
          )
          return onKind ? (
            <button key={k} onClick={() => onKind(k)} className="flex items-center gap-1.5 text-sm rounded-lg px-1.5 py-0.5 hover:bg-primary/5" title="View these alerts">
              {inner}
            </button>
          ) : (
            <span key={k} className="flex items-center gap-1.5 text-sm">{inner}</span>
          )
        })}
      </div>
      <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-container-light dark:bg-container-dark">
        {BANDS.map((b) => {
          const v = a.byBand[b.key] ?? 0
          const pct = a.bandTotal > 0 ? (v / a.bandTotal) * 100 : 0
          return v > 0 ? <div key={b.key} className="h-full" style={{ width: `${pct}%`, backgroundColor: b.hex }} title={`${b.label}: ${v}`} /> : null
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {BANDS.map((b) => (
          <span key={b.key} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: b.hex }} />
            <span className="text-text-light dark:text-text-dark">{b.label}</span>
            <span className="text-subtle-light dark:text-subtle-dark">{a.byBand[b.key] ?? 0}</span>
          </span>
        ))}
      </div>
    </Card>
  )
}
